import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  trackBannerCardClicked,
  trackDiscoveryScreenView,
  trackFeaturedCardClicked,
  trackGridCardClicked,
} from '@/features/discovery/analytics'
import { DiscoveryCard } from '@/features/discovery/components/discovery-card'
import { DiscoverySkeleton } from '@/features/discovery/components/discovery-skeleton'
import { FeaturedCard } from '@/features/discovery/components/featured-card'
import { NoItemsView } from '@/features/discovery/components/no-items-view'
import { useDiscoveryItems } from '@/features/discovery/hooks/use-discovery'
import type { DiscoveryItemCode } from '@/generated/graphql'
import { PageHeader } from '@/shared/components/page-header'

export function DiscoveryPage() {
  const { t } = useTranslation()
  const { data, isLoading } = useDiscoveryItems()

  const featured = data?.discoveryItems?.featured[0]
  const items = data?.discoveryItems?.items ?? []

  const verticalItems = items.filter((i) => i.code !== 'PRINTABLE_PLAN')
  const horizontalItems = items.filter((i) => i.code === 'PRINTABLE_PLAN')

  useEffect(() => {
    if (data) {
      trackDiscoveryScreenView(verticalItems.length, featured?.code)
    }
  }, [data, verticalItems.length, featured?.code])

  const openItem = (url: string) => {
    window.open(url, '_blank')
  }

  // Card: 10.5rem (w-42), gap: 0.5rem (gap-2), min 27.875rem, max 43.625rem
  const CARD_W = 10.5
  const GAP = 0.5
  const MIN_W = 27.875
  const MAX_W = 43.625

  const desktopWidth = useMemo(() => {
    const count = verticalItems.length || 3
    const raw = count * CARD_W + (count - 1) * GAP
    return Math.max(MIN_W, Math.min(MAX_W, raw))
  }, [verticalItems.length])

  return (
    <div className="relative h-full">
      <div className="lg:hidden">
        <PageHeader title={t('discover_screen_title')} />
      </div>

      <div className="h-full overflow-y-auto">
        <div
          className="mx-auto flex max-w-lg flex-col gap-[24px] px-[24px] pt-17 pb-[128px] lg:max-w-[var(--desktop-w)] lg:px-0 lg:pt-8 lg:pb-16"
          style={{ '--desktop-w': `${desktopWidth}rem` } as React.CSSProperties}
        >
          {isLoading ? (
            <>
              <p className="mx-auto max-w-94 text-center text-base leading-6 text-app-grey-900">
                {t('discover_screen_subtitle')}
              </p>
              <DiscoverySkeleton />
            </>
          ) : !featured && items.length === 0 ? (
            <NoItemsView />
          ) : (
            <>
              <p className="mx-auto max-w-94 text-center text-base leading-6 text-app-grey-900">
                {t('discover_screen_subtitle')}
              </p>

              {/* Featured card */}
              {featured && (
                <FeaturedCard
                  description={featured.description}
                  imageUrl={featured.imageUrl}
                  content={featured.content}
                  onOpen={() => {
                    trackFeaturedCardClicked(featured.code, featured.url)
                    openItem(featured.url)
                  }}
                />
              )}

              {/* Secondary section */}
              {verticalItems.length > 0 && (
                <div className="flex flex-col gap-[16px]">
                  <h2
                    className="text-xs font-semibold uppercase text-app-grey-900 lg:text-center"
                    style={{ letterSpacing: '0.6px' }}
                  >
                    {t('discover_secondary_section_title')}
                  </h2>
                  <div className="-mx-[24px] flex gap-[12px] overflow-x-auto px-[24px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] lg:mx-0 lg:justify-center lg:gap-2 lg:overflow-visible lg:px-0">
                    {verticalItems.map((item, index) => (
                      <div
                        key={item.id}
                        className="w-[calc(50%-6px)] flex-shrink-0 overflow-hidden rounded-[24px] lg:w-42 lg:flex-none"
                      >
                        <DiscoveryCard
                          imageUrl={item.imageUrl}
                          content={item.content}
                          code={item.code as DiscoveryItemCode | null}
                          onOpen={() => {
                            trackGridCardClicked(item.code, item.url, index + 1)
                            openItem(item.url)
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Banner cards */}
              {horizontalItems.map((item) => (
                <DiscoveryCard
                  key={item.id}
                  imageUrl={item.imageUrl}
                  content={item.content}
                  code={item.code as DiscoveryItemCode | null}
                  onOpen={() => {
                    trackBannerCardClicked(item.code, item.url)
                    openItem(item.url)
                  }}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
